/* Viretta – our changes to Impact's own behaviour, kept outside theme.js so a theme update never removes them.
   Loaded with defer BEFORE theme.js: it hooks customElements.define, so each patch is in place before the
   theme's elements are upgraded. Ported 8 Oct 2026 from the hand edits in the old theme.js (Impact 6.6). */
(() => {
  if (window.VirettaThemePatches) return;
  window.VirettaThemePatches = true;

  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.themeVariables?.settings?.reduceDrawerAnimation;

  /* The theme's animation library; it is already loaded by theme.js when a drawer opens. */
  let vendor = null;
  const loadVendor = () => { if (!vendor) import('vendor').then((m) => { vendor = m; }).catch(() => {}); };

  const patches = {
    /* 1. Filter drawer: a short slide + fade instead of the clip-path wipe, and it stays where it is in the DOM
          (the old edit meant to keep only the filter drawer out of <body>). */
    'facet-drawer': (FacetDrawer) => {
      loadVendor();
      const proto = FacetDrawer.prototype;
      const originalShow = proto._showTransition;
      const originalHide = proto._hideTransition;
      Object.defineProperty(proto, 'shouldAppendToBody', { get: () => false, configurable: true });

      proto._getFacetDrawerMotion = function () {
        switch (this.openFrom) {
          case 'left': return { key: 'x', enter: [-28, 0], exit: [0, -28] };
          case 'right': return { key: 'x', enter: [28, 0], exit: [0, 28] };
          case 'top': return { key: 'y', enter: [-28, 0], exit: [0, -28] };
          default: return { key: 'y', enter: [28, 0], exit: [0, 28] };
        }
      };

      proto._showTransition = function (animated = true) {
        if (!vendor) return originalShow.call(this, animated);
        let controls;
        this._setInitialPosition();
        if (reduceMotion()) {
          controls = vendor.animate(this, { opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.2 });
        } else {
          const content = this.shadowRoot.querySelector('[part="content"]');
          const closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
          const motion = this._getFacetDrawerMotion();
          controls = vendor.timeline([
            [this, { opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: 0.18 }],
            [content, { opacity: [0, 1], [motion.key]: motion.enter }, { at: '<', duration: 0.42, easing: [0.22, 1, 0.36, 1] }],
            [content.children, { opacity: [0, 1] }, { at: '-0.18', duration: 0.2 }],
            [closeButton, { opacity: [0, 1] }, { at: '<', duration: 0.18 }]
          ]);
        }
        animated ? controls.play() : controls.finish();
        return controls.finished.then(() => this.classList.add('show-close-cursor'));
      };

      proto._hideTransition = function () {
        if (!vendor) return originalHide.call(this);
        let controls;
        if (reduceMotion()) {
          controls = vendor.animate(this, { opacity: [1, 0], visibility: ['visible', 'hidden'] }, { duration: 0.2 });
        } else {
          const content = this.shadowRoot.querySelector('[part="content"]');
          const closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
          const motion = this._getFacetDrawerMotion();
          controls = vendor.timeline([
            [closeButton, { opacity: [1, 0] }, { duration: 0.12 }],
            [content.children, { opacity: [1, 0] }, { at: '<', duration: 0.14 }],
            [content, { opacity: [1, 0], [motion.key]: motion.exit }, { at: '<', duration: 0.32, easing: [0.4, 0, 1, 1] }],
            [this, { opacity: [1, 0], visibility: ['visible', 'hidden'] }, { at: '<', duration: 0.18 }]
          ]);
        }
        return controls.finished.then(() => this.classList.remove('show-close-cursor'));
      };
    },

    /* 2. Transparent header fills after 60 px of scrolling (Impact: 500 px). Same logic as Impact 7.2,
          with the native Web Animations API so it works before the animation library has loaded. */
    'store-header': (StoreHeader) => {
      const OFFSET = 60;
      StoreHeader.prototype._checkTransparency = function () {
        const slide = (from, to) => this.animate({ transform: [from, to] }, { duration: 150, easing: 'ease' });
        if (CSS.supports('selector(:has(> *))') && this.hasAttribute('sticky') && !this.hasAttribute('hide-on-scroll') && this.hasAttribute('allow-transparency')) {
          if (this.querySelectorAll('[open]').length > 0) {
            this.classList.add('is-filled');
          } else if (window.scrollY >= OFFSET && !this._hasSwitchedToSticky) {
            this._hasSwitchedToSticky = true;
            this.classList.add('is-filled');
            slide('translateY(-100%)', 'translateY(0)');
          } else if (window.scrollY < OFFSET) {
            if (this._hasSwitchedToSticky) {
              this._hasSwitchedToSticky = false;
              slide('translateY(0)', 'translateY(-100%)').finished.then(() => {
                this.style.transform = null;
                this.classList.remove('is-filled');
              });
            } else if (this.getAnimations().length === 0) {
              this.classList.remove('is-filled');
            }
          }
        } else {
          const fallbackScroll = this.hasAttribute('sticky') && window.scrollY > 20;
          this.classList.toggle('is-filled', !this.hasAttribute('allow-transparency') || this.querySelectorAll('[open]').length > 0 || fallbackScroll);
        }
      };
    }
  };

  const define = CustomElementRegistry.prototype.define;
  customElements.define = function (name, constructor, options) {
    if (patches[name]) { try { patches[name](constructor); } catch (e) { /* keep the stock behaviour */ } }
    return define.call(this, name, constructor, options);
  };

  /* 3. Add to cart: send the quantity the customer actually chose. Our product page, the sticky bar and quick add
        keep their quantity inputs outside the <form>, so the theme's FormData can miss them. */
  const submitters = new WeakMap();
  document.addEventListener('submit', (event) => {
    if (event.target.matches?.('form[is="product-form"]')) submitters.set(event.target, event.submitter || null);
  }, true);

  document.addEventListener('formdata', (event) => {
    const form = event.target;
    if (!form.matches?.('form[is="product-form"]')) return;
    const submitter = submitters.get(form);
    submitters.delete(form);
    const id = form.getAttribute('id');
    const selector = id
      ? `.quantity-selector__input[form="${CSS.escape(id)}"], #${CSS.escape(`${id}-quantity`)}`
      : '.quantity-selector__input';
    const fromSubmitter = submitter
      ?.closest('[data-sticky-product-form], .product-quick-add, .quantity__selector-and-buy-now-wrapper, .product-info')
      ?.querySelector('[data-sticky-quantity-sync], .quick-add-quantity__input, .quantity-selector__input');
    const fromWrapper = form.closest('.quantity__selector-and-buy-now-wrapper, .product-info')?.querySelector(selector);
    const fromVisible = Array.from(document.querySelectorAll(selector)).find((input) => !input.disabled && input.offsetParent !== null);
    const fromHidden = form.querySelector('[data-product-form-quantity]');
    const quantity = fromSubmitter?.value || fromWrapper?.value || fromVisible?.value || fromHidden?.value;
    if (quantity) event.formData.set('quantity', quantity);
  }, true);

  /* 5. Loading state while a variant change loads (Release 1). Impact fetches the new variant's HTML (~2 s on our
        product pages) and only then updates the form, so until it arrives add to cart would still add the OLD
        variant. The section gets .viretta-is-loading (after 150 ms, so fast changes don't flicker), which dims the
        price and locks the buy buttons; it is cleared when the new HTML is in, or after 8 s as a safety net. */
  const LOADING = 'viretta-is-loading';
  const loadingTimers = new Map(); // scope -> { show, hide }
  const loadingScope = (el) => el.closest('quick-buy-drawer, .quick-buy-drawer, .shopify-section') || document.body;
  const endLoading = (scope) => {
    const timers = loadingTimers.get(scope);
    if (timers) { clearTimeout(timers.show); clearTimeout(timers.hide); loadingTimers.delete(scope); }
    scope.classList.remove(LOADING);
    scope.removeAttribute('aria-busy');
  };
  document.addEventListener('change', (event) => {
    if (!event.target.closest?.('variant-picker')) return;
    const scope = loadingScope(event.target);
    endLoading(scope);
    loadingTimers.set(scope, {
      show: setTimeout(() => { scope.classList.add(LOADING); scope.setAttribute('aria-busy', 'true'); }, 150),
      hide: setTimeout(() => endLoading(scope), 8000)
    });
  }, true);
  document.addEventListener('product:rerender', () => {
    queueMicrotask(() => Array.from(loadingTimers.keys()).forEach(endLoading));
  }, true);

  /* 4. Variant change: also refresh our product badges block (Impact only refreshes its own block types).
        Runs after the theme's own handler, and only for blocks the theme left in place. */
  document.addEventListener('product:rerender', (event) => {
    const fragment = event.detail?.htmlFragment;
    if (!fragment?.querySelector) return;
    queueMicrotask(() => {
      document.querySelectorAll('product-rerender [data-block-type="product-badges"][data-block-id]').forEach((block) => {
        const match = fragment.querySelector(`[data-block-type="product-badges"][data-block-id="${CSS.escape(block.dataset.blockId)}"]`);
        if (match && match !== block) block.replaceWith(match);
      });
    });
  }, true);
})();

// UX review 10 Oct: the free-shipping bar formats with Intl (»200,00 kr.«) – whole kroner like the rest of the shop (»200 kr«)
if (window.customElements) {
  customElements.whenDefined('free-shipping-bar').then(() => {
    const C = customElements.get('free-shipping-bar');
    if (!C || C.prototype.__virettaWhole) return;
    const orig = C.prototype._updateMessage;
    C.prototype.__virettaWhole = true;
    C.prototype._updateMessage = function () {
      this._currencyFormatter = { format: (n) => Math.round(n).toLocaleString('de-DE') + ' €' };
      return orig.apply(this, arguments);
    };
    document.querySelectorAll('free-shipping-bar').forEach((el) => { try { el._updateMessage(); } catch (e) {} });
  });
}

// Round 84 (11 Oct): the Shōjin page's hotspot tooltips print »799,00 kr« (sections/sticky-info-secondary uses | money) –
// whole kroner like the rest of the shop (»799 kr«).
(function () {
  var whole = function () {
    document.querySelectorAll('.aira-tooltip-price').forEach(function (el) {
      var t = el.textContent, n = t.replace(/,00(\s*kr)/, '$1');
      if (n !== t) el.textContent = n;
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', whole); else whole();
})();

// Round 89-P (12 Oct): the phone menu is the v2 menu (snippets/v2-mega-mobile) – it has no ».panel« elements. Impact's
// navigation drawer resets its panels every time it closes and threw »Cannot read properties of null (reading 'style')«
// on every close of the menu. Without panels there is nothing to reset.
if (window.customElements) {
  customElements.whenDefined('navigation-drawer').then(function () {
    var C = customElements.get('navigation-drawer');
    if (!C || C.prototype.__virettaNoPanels || typeof C.prototype.reinitializeDrawer !== 'function') return;
    var orig = C.prototype.reinitializeDrawer;
    C.prototype.__virettaNoPanels = true;
    C.prototype.reinitializeDrawer = function () {
      var megaDesktop = this.hasAttribute('mega-menu') && window.matchMedia('(min-width:1150px)').matches;
      if (!megaDesktop && !this.querySelector('.panel:first-child')) return;
      return orig.apply(this, arguments);
    };
  });
}
